import React from 'react'
import { type MenuOption } from '@opallabs/concepts'
import { Spacing } from '@opallabs/gem'
import { MultiCheckSelect, type MultiCheckSelectProps } from '..'
import { AvatarList } from '../../AvatarList'
import { type AvatarListSortMode } from '../../AvatarList/types'
import { multiUserSelect } from '../styles'

type MultiUserSelectProps = MultiCheckSelectProps<MenuOption> & {
  /**
   * @note When `"reverse"`, `selectedItems` will be reversed so the last items render first, and `<AvatarList>` will be
   * called with `sort="presorted"`.
   * @see `<AvatarList>` `sortMode` prop
   */
  selectedItemsSortMode?: AvatarListSortMode | 'reverse'
}

type SelectedItemsProps = Readonly<{
  placeholder?: string
  selectedItems: MenuOption[]
  sortMode?: MultiUserSelectProps['selectedItemsSortMode']
}>

const SelectedItems: React.FC<SelectedItemsProps> = ({
  placeholder = 'Select users and groups…',
  selectedItems: items,
  sortMode,
}) => {
  const orderedItems = sortMode === 'reverse' ? items.toReversed() : items
  const forwardedSortMode: AvatarListSortMode | undefined =
    sortMode === 'reverse' ? 'presorted' : sortMode

  return !items.length ? (
    <span>{placeholder}</span>
  ) : (
    <AvatarList
      avatarSize={Spacing.L}
      items={orderedItems}
      sortMode={forwardedSortMode}
    />
  )
}
SelectedItems.displayName = 'Lapidary: SelectedItems ← MultiUserSelect'

export const MultiUserSelect: React.FC<MultiUserSelectProps> = (props) => {
  const { placeholder, selectedItemsSortMode, ...rest } = props

  return (
    <MultiCheckSelect css={multiUserSelect} {...rest} asUserSelect={true}>
      {({ selectedItems }) => (
        <SelectedItems
          placeholder={placeholder}
          selectedItems={selectedItems}
          sortMode={selectedItemsSortMode}
        />
      )}
    </MultiCheckSelect>
  )
}
MultiUserSelect.displayName = 'Lapidary: MultiUserSelect'
